/**
 * RestoID — Error / empty state.
 * ---------------------------------------------------------------------------
 * RestoID-styled replacement for the upstream ErrorPage (same prop contract, so
 * it drops into every `<ErrorPage .../>` usage). Reuses the upstream `SupportInfo`.
 * Rendered inside the AppLayout card, so it sits in the RestoID glass surface.
 */
import type { TFuncKey } from 'i18next';
import { type To } from 'react-router-dom';

import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import SupportInfo from '@/pages/ErrorPage/SupportInfo';
import DynamicT from '@/shared/components/DynamicT';
import PageMeta from '@/shared/components/PageMeta';

import { Button, C, FONT, Icon, LinkText, Stack } from '../components/restoid';
import { useCopy } from '../copy';

type Props = {
  readonly title?: TFuncKey;
  readonly message?: TFuncKey;
  readonly rawMessage?: string;
  // Kept for drop-in parity with the upstream ErrorPage (e.g. OneTimeToken/Error
  // passes it); RestoID renders inside the card with no navbar, so it's ignored.
  // eslint-disable-next-line react/no-unused-prop-types
  readonly isNavbarHidden?: boolean;
  // Optional primary CTA — upstream parity (e.g. Consent's access-denied passes a
  // sign-out action). Rendered as the RestoID button.
  readonly primaryAction?: {
    readonly title: TFuncKey;
    readonly to?: To;
    readonly replace?: boolean;
    readonly onClick?: () => void;
  };
};

/** Danger-tinted hero tile. */
const ErrorHero = () => (
  <div style={{ display: 'flex', justifyContent: 'center', margin: '4px 0 2px' }}>
    <span
      style={{
        width: 72,
        height: 72,
        borderRadius: 20,
        background: C.dangerTile,
        border: `1px solid ${C.dangerTileLine}`,
        display: 'grid',
        placeItems: 'center',
        color: C.danger,
      }}
    >
      <Icon name="alert" size={34} sw={1.6} />
    </span>
  </div>
);

const ErrorPage = ({
  title = 'description.not_found',
  message,
  rawMessage,
  primaryAction,
}: Props) => {
  const c = useCopy();
  const navigate = useNavigateWithPreservedSearchParams();
  const hasMessage = Boolean(rawMessage ?? message);

  return (
    <Stack gap={18}>
      <PageMeta titleKey={title} />
      <ErrorHero />
      <div style={{ textAlign: 'center' }}>
        <h2
          style={{
            margin: 0,
            color: C.text,
            fontFamily: FONT,
            fontWeight: 600,
            fontSize: 22,
            lineHeight: 1.25,
            letterSpacing: '-0.02em',
          }}
        >
          <DynamicT forKey={title} />
        </h2>
        {hasMessage ? (
          <p
            style={{
              margin: '10px auto 0',
              color: C.sub,
              font: `400 14px/1.55 ${FONT}`,
              maxWidth: 320,
            }}
          >
            {rawMessage ?? (message ? <DynamicT forKey={message} /> : null)}
          </p>
        ) : null}
      </div>
      {primaryAction ? (
        <Button
          title={<DynamicT forKey={primaryAction.title} />}
          onClick={() => {
            if (primaryAction.onClick) {
              primaryAction.onClick();
              return;
            }
            if (primaryAction.to) {
              navigate(primaryAction.to, { replace: primaryAction.replace });
            }
          }}
        />
      ) : null}
      {history.length > 1 ? (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <LinkText onClick={() => navigate(-1)}>{c('backShort')}</LinkText>
        </div>
      ) : null}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <SupportInfo />
      </div>
    </Stack>
  );
};

export default ErrorPage;
